import { Quill } from "react-quill-new";
import { QuillEventOptimizionData, QuillImageUploadFunction } from "../types";
import { insertEditorImage } from "./insertEditorImage";
import { resolveEditorImageSrc } from "./resolveEditorImageSrc";
import { setQuillBusy } from "./setQuillBusy";
import { stripClassesAndStylesFromPastedHtml } from "./stripPastedHtml";

export type CapturedPasteData = {
    imageFile: File | null;
    html: string;
    text: string;
    htmlImageSrc: string;
};

const insertImageToQuill = (quill: Quill, imageSrc: string, preferredIndex?: number) => {
    insertEditorImage(quill, imageSrc, preferredIndex);
};

export const stopPasteEvent = (e: ClipboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // @ts-ignore
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
};

const mimeToExt = (mime: string): string => {
    if (mime.includes("png")) return "png";
    if (mime.includes("webp")) return "webp";
    if (mime.includes("gif")) return "gif";
    if (mime.includes("bmp")) return "bmp";
    if (mime.includes("svg")) return "svg";
    return "jpg";
};

const looksLikeImageFile = (file: File): boolean =>
    file.type.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || "");

/** Clipboard File often has empty type/name — normalize so webp + Cloudflare upload work. */
export const normalizePastedImageFile = (file: File, mimeHint = ""): File => {
    const type = (file.type || mimeHint || "image/png").toLowerCase();
    if (!type.startsWith("image/") && !looksLikeImageFile(file)) return file;
    const safeType = type.startsWith("image/") ? type : "image/png";
    const hasExt = Boolean(file.name && file.name.includes("."));
    const name = hasExt ? file.name : `pasted-image.${mimeToExt(safeType)}`;
    if (file.type === safeType && file.name === name) return file;
    return new File([file], name, { type: safeType, lastModified: file.lastModified || Date.now() });
};

const extractFirstImageSrcFromHtml = (rawHtml: string): string => {
    if (!rawHtml || typeof DOMParser === "undefined") return "";
    try {
        const doc = new DOMParser().parseFromString(rawHtml, "text/html");
        const firstImg = doc.querySelector("img");
        return String(firstImg?.getAttribute("src") || "").trim();
    } catch {
        return "";
    }
};

const dataUrlToFile = (dataUrl: string, fallbackName = "pasted-image"): File | null => {
    const match = /^data:([^;,]+)?(;base64)?,(.*)$/i.exec(dataUrl);
    if (!match) return null;

    const mime = String(match[1] || "application/octet-stream").toLowerCase();
    const isBase64 = Boolean(match[2]);
    const payload = String(match[3] || "");
    const ext = mimeToExt(mime);
    const fileName = `${fallbackName}.${ext}`;

    try {
        if (isBase64) {
            const binary = atob(payload);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
            }
            return new File([bytes], fileName, { type: mime, lastModified: Date.now() });
        }

        const decoded = decodeURIComponent(payload);
        return new File([decoded], fileName, { type: mime, lastModified: Date.now() });
    } catch {
        return null;
    }
};

const srcToFile = async (src: string): Promise<File | null> => {
    if (!src) return null;
    if (src.startsWith("data:image/")) return dataUrlToFile(src);
    if (!src.startsWith("blob:") && !/^https?:\/\//i.test(src)) return null;

    try {
        const response = await fetch(src);
        if (!response.ok) return null;
        const blob = await response.blob();
        const mime = String(blob.type || "image/png");
        const ext = mimeToExt(mime);
        return new File([blob], `pasted-image.${ext}`, { type: mime, lastModified: Date.now() });
    } catch {
        return null;
    }
};

/**
 * Must run synchronously inside the paste event handler.
 * After the handler returns, clipboardData.getAsFile() is often null.
 */
export const capturePasteData = (e: ClipboardEvent): CapturedPasteData => {
    const empty: CapturedPasteData = { imageFile: null, html: "", text: "", htmlImageSrc: "" };
    const cd = e.clipboardData;
    if (!cd) return empty;

    const items = Array.from(cd.items || []);
    // Prefer png/jpeg over tiff (macOS screenshot often ships tiff first — browsers can't decode it).
    const preferredTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    let rawImageFile: File | null = null;
    let mimeHint = "";

    const imageItems = items.filter(
        (item) => item.kind === "file" && (!item.type || item.type.startsWith("image/")),
    );
    const pickItem =
        preferredTypes
            .map((type) => imageItems.find((item) => item.type === type))
            .find(Boolean) ||
        imageItems.find((item) => item.type && item.type !== "image/tiff") ||
        imageItems[0];

    if (pickItem) {
        const file = pickItem.getAsFile();
        if (file) {
            rawImageFile = file;
            mimeHint = pickItem.type || file.type || "";
        }
    }

    if (!rawImageFile) {
        const files = Array.from(cd.files || []);
        rawImageFile =
            files.find((f) => preferredTypes.includes(f.type)) ||
            files.find((f) => looksLikeImageFile(f) && f.type !== "image/tiff") ||
            files.find((f) => looksLikeImageFile(f)) ||
            null;
    }

    const html = cd.getData("text/html")?.trim() || "";
    const text = cd.getData("text/plain") || "";

    return {
        imageFile: rawImageFile ? normalizePastedImageFile(rawImageFile, mimeHint) : null,
        html,
        text,
        htmlImageSrc: extractFirstImageSrcFromHtml(html),
    };
};

export const isImagePasteCapture = (captured: CapturedPasteData): boolean => {
    if (captured.imageFile) return true;
    const src = captured.htmlImageSrc;
    return (
        src.startsWith("data:image/") ||
        src.startsWith("blob:") ||
        /^https?:\/\//i.test(src)
    );
};

const getUploadFn = (
    data: QuillEventOptimizionData,
): QuillImageUploadFunction | undefined => {
    if (typeof data.uploadImage === "function") return data.uploadImage;
    if (typeof data.uploadPastedImage === "function") return data.uploadPastedImage;
    return undefined;
};

export const uploadAndInsertImage = async (
    file: File,
    quillOptimizionData: QuillEventOptimizionData,
    preferredIndex?: number,
) => {
    const { quill, webpQuality, maxW, maxH } = quillOptimizionData;
    const index = preferredIndex ?? quill.getSelection()?.index;
    let imageSrc = "";
    try {
        setQuillBusy(quill, true);
        imageSrc = await resolveEditorImageSrc(file, {
            webpQuality,
            maxW,
            maxH,
            uploadImage: getUploadFn(quillOptimizionData),
        });
    } finally {
        setQuillBusy(quill, false);
    }
    insertImageToQuill(quill, imageSrc, index);
};

export const processCapturedPaste = async (
    captured: CapturedPasteData,
    quillOptimizionData: QuillEventOptimizionData,
) => {
    const { quill, handleClipboardImages = true } = quillOptimizionData;
    const preferredIndex = quill.getSelection()?.index;

    if (handleClipboardImages && captured.imageFile) {
        await uploadAndInsertImage(captured.imageFile, quillOptimizionData, preferredIndex);
        return;
    }

    if (handleClipboardImages && isImagePasteCapture(captured)) {
        const fileFromHtmlImage = await srcToFile(captured.htmlImageSrc);
        if (fileFromHtmlImage) {
            await uploadAndInsertImage(
                normalizePastedImageFile(fileFromHtmlImage),
                quillOptimizionData,
                preferredIndex,
            );
        }
        return;
    }

    if (!captured.html) return;

    const range = quill.getSelection(true);
    if (range == null) return;

    const html = stripClassesAndStylesFromPastedHtml(captured.html);
    const clipboard = quill.getModule("clipboard") as {
        onPaste: (r: { index: number; length: number }, data: { html?: string; text?: string }) => void;
    };
    clipboard.onPaste(range, { html, text: captured.text });
};

/** @deprecated use capturePasteData + processCapturedPaste — kept for drop path callers */
export const onPasteCapture = async (
    e: ClipboardEvent,
    quillOptimizionData: QuillEventOptimizionData,
) => {
    const captured = capturePasteData(e);
    if (!e.clipboardData) return;
    if (isImagePasteCapture(captured) || captured.html) {
        stopPasteEvent(e);
    }
    await processCapturedPaste(captured, quillOptimizionData);
};

export const onDropCapture = async (
    e: DragEvent,
    quillOptimizionData: QuillEventOptimizionData,
) => {
    if (!e.dataTransfer) return;

    const file = Array.from(e.dataTransfer.files || []).find((f) => looksLikeImageFile(f));
    if (!file) return;

    const preferredIndex = quillOptimizionData.quill.getSelection()?.index;
    e.preventDefault();
    e.stopPropagation();
    // @ts-ignore
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    quillOptimizionData.quill.focus();
    await uploadAndInsertImage(normalizePastedImageFile(file), quillOptimizionData, preferredIndex);
};
